import React, { useEffect, useMemo, useState } from 'react';
import {
  FaArrowLeft,
  FaLaptop,
  FaMobileAlt,
  FaDesktop,
  FaMapMarkerAlt,
  FaClock,
  FaSignOutAlt,
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { graphqlRequest } from '../utils/graphqlRequest';
import '../styles/AdminDeviceSessions.css';

const GET_MY_DEVICE_SESSIONS = `
  query GetMyDeviceSessions {
    getMyDeviceSessions {
      sessionId
      device
      type
      location
      lastActiveAt
      isCurrent
    }
  }
`;

const REVOKE_DEVICE_SESSION = `
  mutation RevokeDeviceSession($sessionId: String!) {
    revokeDeviceSession(sessionId: $sessionId)
  }
`;

const REVOKE_ALL_OTHER_DEVICE_SESSIONS = `
  mutation RevokeAllOtherDeviceSessions {
    revokeAllOtherDeviceSessions
  }
`;

const PAGE_SIZE = 10;

function getDeviceIcon(type) {
  if (type === 'mobile') return FaMobileAlt;
  if (type === 'desktop') return FaDesktop;
  return FaLaptop;
}

export default function DeviceSessions() {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionLoadingId, setActionLoadingId] = useState('');
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  const fetchSessions = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await graphqlRequest(GET_MY_DEVICE_SESSIONS);
      setSessions(Array.isArray(data?.getMyDeviceSessions) ? data.getMyDeviceSessions : []);
      setPage(1);
    } catch (fetchError) {
      setError(fetchError?.message || 'Unable to load device sessions.');
      setSessions([]);
      setPage(1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSessions();
  }, []);

  const formatLastActive = (value, isCurrent) => {
    if (isCurrent) return 'Active now';
    if (!value) return 'Unknown';
    const ts = new Date(value).getTime();
    if (Number.isNaN(ts)) return 'Unknown';
    const diffMs = Date.now() - ts;
    const diffMinutes = Math.floor(diffMs / 60000);
    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
    return new Date(ts).toLocaleString();
  };

  const handleRevoke = async (sessionId) => {
    const target = sessions.find((s) => s.sessionId === sessionId);
    if (!target || target.isCurrent) return;
    try {
      setActionLoadingId(sessionId);
      setError('');
      await graphqlRequest(REVOKE_DEVICE_SESSION, { sessionId });
      setSessions((prev) => prev.filter((s) => s.sessionId !== sessionId));
    } catch (revokeError) {
      setError(revokeError?.message || 'Unable to revoke session.');
    } finally {
      setActionLoadingId('');
    }
  };

  const handleRevokeAllOthers = async () => {
    try {
      setBulkActionLoading(true);
      setError('');
      await graphqlRequest(REVOKE_ALL_OTHER_DEVICE_SESSIONS);
      setSessions((prev) => prev.filter((s) => s.isCurrent));
      setPage(1);
    } catch (revokeError) {
      setError(revokeError?.message || 'Unable to log out from other devices.');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const otherSessions = useMemo(() => sessions.filter((s) => !s.isCurrent), [sessions]);

  const totalPages = Math.max(1, Math.ceil(sessions.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageRows = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return sessions.slice(start, start + PAGE_SIZE);
  }, [sessions, currentPage]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  return (
    <div className="ds-page">
      <div className="ds-topbar">
        <button
          type="button"
          className="ds-topbar-btn"
          onClick={() => navigate(-1)}
          aria-label="Go back"
        >
          <FaArrowLeft />
        </button>
        <h1>Device Sessions</h1>
        <span className="ds-topbar-btn-placeholder" />
      </div>

      <section className="ds-card">
        <div className="ds-card-header">
          <h2>Active Sessions</h2>
          <p className="ds-subtitle">
            Manage devices where you&apos;re logged in. Revoke any session you don&apos;t recognize.
          </p>
        </div>

        <div className="ds-session-list">
          {loading && (
            <div className="ds-session-row">
              <div className="ds-session-info">
                <p className="ds-session-device">Loading sessions...</p>
              </div>
            </div>
          )}

          {!loading && error && (
            <div className="ds-session-row">
              <div className="ds-session-info">
                <p className="ds-session-device">Could not load sessions</p>
                <p className="ds-session-meta">{error}</p>
              </div>
            </div>
          )}

          {!loading && !error && sessions.length === 0 && (
            <div className="ds-session-row">
              <div className="ds-session-info">
                <p className="ds-session-device">No active sessions found.</p>
              </div>
            </div>
          )}

          {!loading && !error && pageRows.map((session) => {
            const Icon = getDeviceIcon(session.type);
            return (
              <div
                key={session.sessionId}
                className={`ds-session-row ${session.isCurrent ? 'current' : ''}`}
              >
                <div className="ds-session-icon">
                  <Icon />
                </div>
                <div className="ds-session-info">
                  <p className="ds-session-device">
                    {session.device}
                    {session.isCurrent && <span className="ds-session-badge">This device</span>}
                  </p>
                  <p className="ds-session-meta">
                    <FaMapMarkerAlt className="ds-meta-icon" />
                    {session.location}
                  </p>
                  <p className="ds-session-meta">
                    <FaClock className="ds-meta-icon" />
                    {formatLastActive(session.lastActiveAt, session.isCurrent)}
                  </p>
                </div>
                {!session.isCurrent && (
                  <button
                    type="button"
                    className="ds-revoke-btn"
                    onClick={() => handleRevoke(session.sessionId)}
                    disabled={actionLoadingId === session.sessionId || bulkActionLoading}
                  >
                    {actionLoadingId === session.sessionId ? 'Revoking...' : 'Revoke'}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {!loading && !error && sessions.length > PAGE_SIZE ? (
          <div className="ds-pagination">
            <span>
              Showing {(currentPage - 1) * PAGE_SIZE + 1}–
              {Math.min(currentPage * PAGE_SIZE, sessions.length)} of{' '}
              {sessions.length}
            </span>
            <div className="ds-pagination-controls">
              <button
                type="button"
                disabled={currentPage <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                aria-label="Previous page"
              >
                ‹
              </button>
              <span className="ds-pagination-page">
                Page {currentPage} of {totalPages}
              </span>
              <button
                type="button"
                disabled={currentPage >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                aria-label="Next page"
              >
                ›
              </button>
            </div>
          </div>
        ) : null}

        {otherSessions.length > 0 && (
          <div className="ds-actions">
            <button
              type="button"
              className="ds-revoke-all-btn"
              onClick={handleRevokeAllOthers}
              disabled={bulkActionLoading || !!actionLoadingId}
            >
              <FaSignOutAlt />
              {bulkActionLoading ? 'Logging out...' : 'Log out from all other devices'}
            </button>
          </div>
        )}
      </section>
    </div>
  );
}
